import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  ellipsizeMiddle,
  IconWrapper,
  resolveCommunityForMember,
  resolveCommunityForSite,
  resolveMemberForSite,
} from '@flexo/general';
import { Autocomplete, ListItem, Popper, TextField } from '@mui/material';
import * as luxon from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { KPI, store } from '@flexo/reducers';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import _ from 'lodash';
import styles from './breadcrumbs-dropdown.module.scss';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

function unFocusChild(childID: string) {
  try {
    const child = document.getElementById(childID);
    (child as any).blur();
  } catch (err: any) {
    console.warn(err);
  }
}

function getTextWidth(text, font) {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  context.font = font; // Specifica il font esatto (es. "16px Arial")
  return context.measureText(text).width;
}

type UseSafariAutoInputWidthParams = {
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  getText: () => string;
  deps: ReadonlyArray<any>;
  minPx?: number; // optional clamp lower bound (not used by default)
  maxPx?: number; // optional clamp upper bound (not used by default)
  offsetPx?: number; // extra pixels to add to measured text width (default 8)
};

function useSafariAutoInputWidth(
  params: UseSafariAutoInputWidthParams
): number {
  const { inputRef, getText, deps, minPx, maxPx, offsetPx = 0 } = params;
  const [px, setPx] = useState<number>(0);

  useLayoutEffect(() => {
    if (!isSafari) return;
    let mirrorEl: HTMLSpanElement | null = null;

    const compute = () => {
      const el = inputRef.current;
      if (!el) return;
      const style = window.getComputedStyle(el);
      const rootStyle = window.getComputedStyle(document.documentElement);
      const resolveVar = (val?: string) => {
        if (!val) return '';
        const m = val.match(/var\((--[^)]+)\)/);
        if (!m) return val;
        const resolved = rootStyle.getPropertyValue(m[1]).trim();
        return resolved || val;
      };

      const fontWeight = style.fontWeight || '600';
      const fontSize = style.fontSize || '18px';
      let fontFamily = style.fontFamily || 'Flexo medium';
      fontFamily = resolveVar(fontFamily);
      const fontStyle = style.fontStyle || 'normal';
      const letterSpacing = style.letterSpacing || '0px';
      const textTransform = style.textTransform || 'none';

      const str = (getText() || '') as string;

      if (!mirrorEl) {
        mirrorEl = document.createElement('span');
        mirrorEl.style.position = 'absolute';
        mirrorEl.style.visibility = 'hidden';
        mirrorEl.style.whiteSpace = 'pre';
        mirrorEl.style.top = '0';
        mirrorEl.style.left = '-10000px';
        mirrorEl.style.pointerEvents = 'none';
        document.body.appendChild(mirrorEl);
      }

      mirrorEl.style.fontFamily = fontFamily as string;
      mirrorEl.style.fontSize = fontSize as string;
      mirrorEl.style.fontWeight = fontWeight as any;
      mirrorEl.style.fontStyle = fontStyle as any;
      mirrorEl.style.letterSpacing = letterSpacing as any;
      mirrorEl.style.textTransform = textTransform as any;
      mirrorEl.textContent = str;

      let width = Math.ceil(mirrorEl.offsetWidth) + offsetPx;
      if (!width || Number.isNaN(width)) {
        const font = `${fontWeight} ${fontSize} ${fontFamily}`;
        width = Math.ceil(getTextWidth(str, font)) + offsetPx;
      }
      if (typeof minPx === 'number' || typeof maxPx === 'number') {
        const lower = typeof minPx === 'number' ? minPx : -Infinity;
        const upper = typeof maxPx === 'number' ? maxPx : Infinity;
        width = Math.max(lower, Math.min(width, upper));
      }
      setPx(width);
    };

    let rafCheck = 0;
    let raf1 = 0;
    let raf2 = 0;

    const runCompute = () => {
      raf1 = requestAnimationFrame(() => {
        compute();
        raf2 = requestAnimationFrame(compute);
      });
    };

    const fonts = (document as any)?.fonts;

    const tryStart = () => {
      if (!inputRef.current) {
        rafCheck = requestAnimationFrame(tryStart);
        return;
      }
      if (fonts?.ready?.then) {
        fonts.ready.then(runCompute);
      } else {
        runCompute();
      }
    };

    tryStart();

    window.addEventListener('resize', compute);
    document.addEventListener('visibilitychange', compute as any);
    return () => {
      window.removeEventListener('resize', compute);
      document.removeEventListener('visibilitychange', compute as any);
      if (rafCheck) cancelAnimationFrame(rafCheck);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (mirrorEl && mirrorEl.parentNode) {
        mirrorEl.parentNode.removeChild(mirrorEl);
        mirrorEl = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSafari, inputRef, ...deps]);

  return px;
}

export function CommunitiesDropDown({
  showCalendar = true,
}: {
  showCalendar?: boolean;
}) {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const user = useSelector((state: any) => state.user);
  const inputRefCommunities = useRef<HTMLInputElement | null>(null);
  const pathSegment = location.pathname.split('/')[1];
  const isEndUser =
    (user?.orgRole || user?.tokenContent?.org_role) === 'org:end_user';
  const isAllCommunitiesRoute = isEndUser && pathSegment === 'communities';

  let communities = (metadata?.entities as any)?.community || [];

  try {
    communities = (_.cloneDeep(communities) || [])?.sort((a, b) =>
      (a?.communityName || '')?.localeCompare(b?.communityName || '')
    );
  } catch (err: any) {
    console.warn(err);
  }

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCommunity, setSelectedCommunity] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      setSelectedCommunity(metadata?.selectedEntity);
    }, 300);
  }, [metadata?.selectedEntity]);

  const allCommunitiesOption = {
    __allCommunities: true,
    communityId: '__all_communities__',
    communityName: t('dropdowns.allCommunities') as string,
    communityExternalId: '',
  };
  const communitiesDateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(calendar.hiveCalendar.selectedDate[0]).toFormat(
        'yyyy-MM-dd'
      )
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');
  const endUserSiteCommunityIds = new Set(
    (((metadata?.entities as any)?.site || []) as any[])
      .map(
        (site: any) =>
          resolveCommunityForSite(site, communitiesDateFrom) ||
          site?.communityId
      )
      .filter(Boolean)
  );
  const canUseAllCommunities = isEndUser && endUserSiteCommunityIds.size > 1;
  const communitiesWithAll = canUseAllCommunities
    ? [allCommunitiesOption, ...communities]
    : communities;

  const communityName = isAllCommunitiesRoute
    ? (t('dropdowns.allCommunities') as string)
    : (metadata?.selectedEntity as any)?.communityName || '';
  const displayName = showCalendar
    ? ellipsizeMiddle(communityName)
    : communityName;

  const inputPx = useSafariAutoInputWidth({
    inputRef: inputRefCommunities,
    getText: () => searchTerm || displayName,
    deps: [searchTerm, communityName, showCalendar],
  });
  const goToCommunityOption = (option: any) => {
    if (!option) return;

    if (isEndUser && option.__allCommunities) {
      store.dispatch(KPI.ResetKpiFields());
      [
        'EnergyCommunity',
        'EconomicsCommunity',
        'AncillaryCommunity',
        'ComparisonCommunity',
        'EnergyKpiDataCommunityAsyncStatus',
        'EconomicsKpiDataCommunityAsyncStatus',
        'AncillaryKpiDataCommunityAsyncStatus',
        'ComparisonKpiDataCommunityAsyncStatus',
      ].forEach((field) => store.dispatch(KPI.ResetNamedKpiFields(field)));
      navigate(`/communities${location.search || ''}`);
    } else if (params?.id !== option.communityId) {
      navigateToCommunity(`community`, option.communityId);
    }

    unFocusChild('dropdown-communities');
  };

  // Custom filter function that searches across multiple fields
  const filterOptions = (options: any[], state: { inputValue: string }) => {
    const term = (state?.inputValue || '').toLowerCase();
    if (!term) return options;

    return options.filter((option) => {
      if (!option) return false;

      const communityName = String(option?.communityName ?? '').toLowerCase();
      const communityCode = String(option?.communityCode ?? '').toLowerCase();
      const communityExternalId = String(
        option?.communityExternalId ?? ''
      ).toLowerCase();
      const communityId = String(option?.communityId ?? '').toLowerCase();

      return (
        communityName.includes(term) ||
        communityCode.includes(term) ||
        communityExternalId.includes(term) ||
        communityId.includes(term)
      );
    });
  };

  return selectedCommunity ? (
    <Autocomplete
      disablePortal={!isEndUser}
      id="dropdown-communities"
      title={(metadata?.selectedEntity as any)?.communityCode || ''}
      disabled={communities.length <= 1}
      clearOnBlur={true}
      className={`${styles.BreadCrumb} `}
      options={communitiesWithAll || []}
      filterOptions={filterOptions}
      getOptionLabel={(option: any) => option?.communityName || ''}
      onClose={() => setSearchTerm('')}
      noOptionsText={
        <span className={`${styles.Autocomplete__List__Item}`}>
          {t('widgets.search.no_results')}
        </span>
      }
      onKeyUp={(e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e?.target?.value) {
            const option =
              communitiesWithAll.find(
                (community: any) =>
                  community.communityName === e.target?.value || ''
              ) || null;
            if (option) {
              goToCommunityOption(option);
            }
          }
        }
      }}
      popupIcon={
        communities?.length > 1 ? (
          <IconWrapper
            iconName={'chevron-right'}
            className={`${styles.BreadCrumb__Icon} ${
              isSafari ? styles.BreadCrumb__Icon__Safari : ''
            }`}
          />
        ) : null
      }
      renderOption={(props, option, { selected }) => (
        <ListItem
          {...props}
          key={uuidv4()}
          className={`${
            (option?.__allCommunities && isAllCommunitiesRoute) ||
            option?.communityId === params.id
              ? styles.Autocomplete__List__Item__Active
              : styles.Autocomplete__List__Item
          }`}
          value={option.value}
        >
          <span
            style={{
              paddingLeft: 24,
              cursor:
                !option?.__allCommunities && params.id === option.communityId
                  ? 'default'
                  : 'pointer',
            }}
            onClick={() => goToCommunityOption(option)}
          >
            {ellipsizeMiddle(option?.communityName || '', [22, 14, 14])}
            {!option?.__allCommunities && (
              <>
                <br />
                <small>
                  {' '}
                  {ellipsizeMiddle(option?.communityExternalId || '')}{' '}
                </small>
              </>
            )}
          </span>
        </ListItem>
      )}
      PopperComponent={({ children, ...props }) => {
        return (
          <Popper
            {...props}
            id="breadcrumbs-popper"
            className={`${isEndUser ? 'enduser-breadcrumbs-popper' : ''} ${
              isSafari ? 'is_safari_popper communities' : ''
            } ${(searchTerm || '')?.length > 0 ? 'typing' : ''}`}
            style={{
              width: 300,
              alignSelf: 'flex-end',
              borderRadius: 12,
              marginTop: 8,
              ...(isEndUser ? { zIndex: 20000001 } : {}),
            }}
            anchorEl={document.getElementById('dropdown-communities')}
            placement={isEndUser ? 'bottom-start' : 'left'}
            modifiers={
              isEndUser
                ? [
                    {
                      name: 'offset',
                      options: { offset: [0, 40] },
                    },
                    {
                      name: 'preventOverflow',
                      options: { padding: 16 },
                    },
                  ]
                : undefined
            }
          >
            {children}
          </Popper>
        );
      }}
      renderInput={(params) => {
        return selectedCommunity ? (
          <TextField
            {...params}
            variant="standard"
            id="autocomplete-communities-input"
            className={styles.BreadCrumb__Input}
            onChange={(ev: any) => setSearchTerm(ev.target.value)}
            placeholder={communities?.length > 1 ? displayName : communityName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = document.querySelector(
                  '#breadcrumbs-popper [role="option"]'
                ) as HTMLElement | null;
                if (first) {
                  e.preventDefault();
                  first.click();
                }
              }
            }}
            label={communities?.length > 1 ? displayName : communityName}
            inputRef={(el) => {
              inputRefCommunities.current = el;
              const r = (params.inputProps as any).ref;
              if (typeof r === 'function') r(el);
              else if (r && 'current' in r)
                (r as React.MutableRefObject<HTMLInputElement | null>).current =
                  el;
            }}
            inputProps={{
              ...params.inputProps,
              ...(isSafari
                ? { style: { width: inputPx + 3.5 + 'px' } as any }
                : {}),
            }}
          ></TextField>
        ) : (
          <></>
        );
      }}
    />
  ) : (
    <></>
  );
}

export function MembersDropDown({
  showCalendar = true,
}: {
  showCalendar?: boolean;
}) {
  const { t } = useTranslation();
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const params = useParams();
  const inputRefMember = useRef<HTMLInputElement | null>(null);

  const [searchTerm, setSearchTerm] = useState<string | null>();

  function getMemberName(datum: any, applyEllipsis = true) {
    if (!datum) {
      datum = metadata?.entities?.member?.find(
        (member: any) => member?.memberId === params.id
      );
    }
    const fullName = `${datum?.memberFirstName || ''} ${
      datum?.memberLastName || ''
    }`.trim();
    return applyEllipsis ? ellipsizeMiddle(fullName, [20, 12, 12]) : fullName;
  }

  const memberName = getMemberName(null, showCalendar);

  const dateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(calendar.hiveCalendar.selectedDate[0]).toFormat(
        'yyyy-MM-dd'
      )
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');

  const currentMember = metadata?.entities?.member?.find(
    (member: any) => member?.memberId === params.id
  );
  const memberCommunityID = resolveCommunityForMember(currentMember, dateFrom);

  const inputPx = useSafariAutoInputWidth({
    inputRef: inputRefMember,
    getText: () =>
      searchTerm || memberName || (inputRefMember.current as any)?.value || '',
    deps: [searchTerm, memberName, showCalendar],
  });

  // Merge entities.member with categorized members (current + previous + future)
  const entitiesMembers = metadata?.entities?.member || [];
  const categorizedMembers = [
    ...(metadata?.member?.current || []),
    ...(metadata?.member?.previous || []),
    ...(metadata?.member?.future || []),
  ];

  const memberMap = new Map();
  entitiesMembers.forEach((m: any) => memberMap.set(m.memberId, m));
  categorizedMembers.forEach((m: any) => {
    if (!memberMap.has(m.memberId)) memberMap.set(m.memberId, m);
  });

  let parsedList = Array.from(memberMap.values()).filter(
    (member: any) =>
      resolveCommunityForMember(member, dateFrom) === memberCommunityID
  );

  try {
    parsedList = (_.cloneDeep(parsedList) || [])?.sort((a, b) =>
      (`${a?.memberFirstName} ${a?.memberLastName}` || '')?.localeCompare(
        `${b?.memberFirstName} ${b?.memberLastName}` || ''
      )
    );
  } catch (err: any) {
    console.warn(err);
  }

  // Custom filter function that searches across multiple fields
  const filterOptions = (options: any[], state: { inputValue: string }) => {
    const term = (state?.inputValue || '').toLowerCase();
    if (!term) return options;

    return options.filter((option) => {
      if (!option) return false;

      const communityId = String(option?.communityId ?? '').toLowerCase();
      const email = String(option?.email ?? '').toLowerCase();
      const memberExternalId = String(
        option?.memberExternalId ?? ''
      ).toLowerCase();
      const memberFirstName = String(
        option?.memberFirstName ?? ''
      ).toLowerCase();
      const memberId = String(option?.memberId ?? '').toLowerCase();
      const memberLastName = String(option?.memberLastName ?? '').toLowerCase();
      const phone = String(option?.phone ?? '').toLowerCase();
      const fullName = `${option?.memberFirstName ?? ''} ${
        option?.memberLastName ?? ''
      }`.toLowerCase();

      return (
        communityId.includes(term) ||
        email.includes(term) ||
        memberExternalId.includes(term) ||
        memberFirstName.includes(term) ||
        memberId.includes(term) ||
        memberLastName.includes(term) ||
        phone.includes(term) ||
        fullName.includes(term)
      );
    });
  };

  return (
    <Autocomplete
      // disablePortal
      // open={true}
      disablePortal={true}
      id="autocomplete-members"
      title={currentMember?.memberExternalId || ''}
      clearOnBlur={true}
      className={`${styles.BreadCrumb}`}
      options={parsedList || []}
      filterOptions={filterOptions}
      // groupBy={(option: any) => option.timeSpan}
      onClose={() => setSearchTerm('')}
      sx={{
        width: 'auto',
        '& .MuiAutocomplete-input': {
          width: 'auto',
          borderRadius: '0px!important',
          cursor: 'pointer', // maxWidth: 300,
          // minWidth: '200px', // Imposta una larghezza minima se necessario
        },
      }}
      onKeyUp={(e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e?.target?.value) {
            const option =
              parsedList.find(
                (member: any) =>
                  ellipsizeMiddle(getMemberName(member)) === e.target?.value ||
                  ''
              ) || null;
            if (option) {
              navigateToMember('member', option.memberId);
              unFocusChild('autocomplete-members');
            }
          }
        }
      }}
      getOptionLabel={(option: any) => getMemberName(option) as string}
      popupIcon={
        <IconWrapper
          iconName={'chevron-right'}
          className={`${styles.BreadCrumb__Icon} ${
            isSafari ? styles.BreadCrumb__Icon__Safari : ''
          }`}
        />
      }
      PopperComponent={({ children, ...props }) => {
        return (
          <Popper
            {...props}
            id="breadcrumbs-popper"
            className={`${isSafari ? 'is_safari_popper member' : ''} ${
              (searchTerm || '')?.length > 0 ? 'typing' : ''
            }`}
            style={{
              width: 300,
              alignSelf: 'flex-end',
              borderRadius: 12,
              marginTop: 8,
            }}
            placement={'left'}
            anchorEl={document.getElementById('autocomplete-members')}
          >
            {children}
          </Popper>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            variant="standard"
            onChange={(ev: any) => setSearchTerm(ev.target.value)}
            id="autocomplete-members-input"
            className={styles.BreadCrumb__Input}
            label={memberName || (t('widgets.dropdowns.members') as string)}
            placeholder={
              memberName || (t('widgets.dropdowns.members') as string)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = document.querySelector(
                  '#breadcrumbs-popper [role="option"]'
                ) as HTMLElement | null;
                if (first) {
                  e.preventDefault();
                  first.click();
                }
              }
            }}
            inputRef={(el) => {
              inputRefMember.current = el;
              const r = (params.inputProps as any).ref;
              if (typeof r === 'function') r(el);
              else if (r && 'current' in r)
                (r as React.MutableRefObject<HTMLInputElement | null>).current =
                  el;
            }}
            inputProps={{
              ...params.inputProps,
              ...(isSafari
                ? { style: { width: inputPx + 3.5 + 'px' } as any }
                : {}),
            }}
          ></TextField>
        );
      }}
      renderOption={(props, option, { selected }) => {
        return (
          <ListItem
            {...props}
            className={`${
              option?.memberId === params.id
                ? styles.Autocomplete__List__Item__Active
                : styles.Autocomplete__List__Item
            }`}
            value={option.value}
          >
            <span
              style={{ paddingLeft: 24, cursor: 'pointer' }}
              onClick={(ev: any) => (
                navigateToMember('member', option.memberId),
                unFocusChild('autocomplete-members')
              )}
            >
              {getMemberName(option, true)}
              <br />
              <small> {ellipsizeMiddle(option?.memberExternalId)} </small>
            </span>
          </ListItem>
        );
      }}
    />
  );
}

export function SitesDropDown({
  showCalendar = true,
}: {
  showCalendar?: boolean;
}) {
  const { t } = useTranslation();

  const params = useParams();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const user = useSelector((state: any) => state.user);
  // Inlined to avoid an atoms -> hooks dependency cycle.
  const isEndUser =
    (user?.orgRole || user?.tokenContent?.org_role) === 'org:end_user';
  const [searchTerm, setSearchTerm] = useState<string>('');

  const isMemberAggregate = isEndUser && pathSegment === 'member';
  const siteName = isMemberAggregate
    ? (t('dropdowns.allSites') as string)
    : (metadata?.selectedEntity as any)?.siteName || '';
  const displaySiteName = showCalendar ? ellipsizeMiddle(siteName) : siteName;

  const inputRefSites = useRef<HTMLInputElement | null>(null);
  const inputPx = useSafariAutoInputWidth({
    inputRef: inputRefSites,
    getText: () =>
      searchTerm ||
      displaySiteName ||
      (inputRefSites.current as any)?.value ||
      '',
    deps: [searchTerm, siteName, showCalendar],
  });

  const sitesDateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(calendar.hiveCalendar.selectedDate[0]).toFormat(
        'yyyy-MM-dd'
      )
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');

  const entitiesMembers = (metadata?.entities as any)?.member || [];
  const categorizedMembers = [
    ...(metadata?.member?.current || []),
    ...(metadata?.member?.previous || []),
    ...(metadata?.member?.future || []),
  ];
  const memberMap = new Map();
  entitiesMembers.forEach((member: any) =>
    memberMap.set(member.memberId, member)
  );
  categorizedMembers.forEach((member: any) => {
    if (!memberMap.has(member.memberId)) {
      memberMap.set(member.memberId, member);
    }
  });

  const allMembers = isEndUser
    ? Array.from(memberMap.values())
    : entitiesMembers;
  const allSites = (metadata?.entities as any)?.site || [];
  const siteRouteContext = isMemberAggregate ? 'site' : pathSegment;
  const resolvedMemberId = isMemberAggregate
    ? (params.id as string)
    : resolveMemberForSite(allMembers, params.id as string, sitesDateFrom);

  const selectedMember = allMembers.find(
    (member: any) => member?.memberId === resolvedMemberId
  );
  const memberSiteIds = new Set(
    [
      ...((selectedMember?.sites as any[]) || []).map(
        (site: any) => site?.siteId
      ),
      ...((selectedMember?.changelog as any[]) || []).map(
        (entry: any) => entry?.siteId
      ),
    ].filter(Boolean)
  );

  const selectedMemberSites = ((selectedMember?.sites as any[]) || []).filter(
    (site: any) => site?.siteId
  );
  const uniqueSites = (sites: any[]) =>
    Array.from(
      new Map(
        (sites || [])
          .filter((site: any) => site?.siteId)
          .map((site: any) => [site.siteId, site])
      ).values()
    );

  const sitesList =
    isMemberAggregate && resolvedMemberId
      ? uniqueSites([
          ...allSites.filter((site: any) =>
            memberSiteIds.size > 0
              ? memberSiteIds.has(site?.siteId)
              : site?.memberId === resolvedMemberId
          ),
          ...selectedMemberSites,
        ])
      : resolvedMemberId
      ? allSites.filter((site: any) => {
          const siteMember = resolveMemberForSite(
            allMembers,
            site.siteId,
            sitesDateFrom
          );
          return siteMember === resolvedMemberId;
        })
      : allSites.filter((s: any) => s.siteId === params.id);

  // FLEXO-6066: end users get an "All sites" entry on the /member aggregate
  // and on multi-site pages, so the selector can switch aggregate <-> site.
  const allSitesOption = {
    __allSites: true,
    siteId: '__all_sites__',
    siteName: t('dropdowns.allSites') as string,
    pod: t('dropdowns.allSites') as string,
    memberId: resolvedMemberId,
  };
  const sitesListWithAll =
    isEndUser &&
    resolvedMemberId &&
    sitesList.length > 0 &&
    sitesList.length > 1
      ? [allSitesOption, ...sitesList]
      : sitesList;
  const hasSiteOptions = sitesListWithAll.length > 1;
  const goToSiteOption = (option: any) => {
    if (!option) return;

    if (isEndUser && option.__allSites) {
      navigateToMember('member', resolvedMemberId);
    } else {
      navigateToSite(siteRouteContext, option.siteId);
    }

    unFocusChild('autocomplete-sites');
  };

  // Custom filter function that searches across multiple fields
  const filterOptions = (options: any[], state: { inputValue: string }) => {
    const term = (state?.inputValue || '').toLowerCase();
    if (!term) return options;

    return options.filter((option) => {
      if (!option) return false;

      const communityId = String(option?.communityId ?? '').toLowerCase();
      const memberId = String(option?.memberId ?? '').toLowerCase();
      const pod = String(option?.pod ?? '').toLowerCase();
      const siteAddress = String(option?.siteAddress ?? '').toLowerCase();
      const siteExternalId = String(option?.siteExternalId ?? '').toLowerCase();
      const siteId = String(option?.siteId ?? '').toLowerCase();
      const siteName = String(option?.siteName ?? '').toLowerCase();
      const siteType = String(
        option?.siteType?.[0]?.siteType ?? ''
      ).toLowerCase();

      return (
        communityId.includes(term) ||
        memberId.includes(term) ||
        pod.includes(term) ||
        siteAddress.includes(term) ||
        siteExternalId.includes(term) ||
        siteId.includes(term) ||
        siteName.includes(term) ||
        siteType.includes(term)
      );
    });
  };

  return (
    <Autocomplete
      disablePortal={!isEndUser}
      // disablePortal
      id="autocomplete-sites"
      title={(metadata?.selectedEntity as any)?.pod || ''}
      clearOnBlur={true}
      className={`${styles.BreadCrumb}`}
      options={sitesListWithAll || []}
      filterOptions={filterOptions}
      // groupBy={(option: any) => option.timeSpan}
      sx={{
        width: 'auto',
        '& .MuiAutocomplete-input': {
          width: 'auto',
          cursor: 'pointer',
          borderRadius: '0px!important',
        },
      }}
      onKeyUp={(e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e?.target?.value) {
            const option =
              sitesListWithAll.find(
                (site: any) => site.pod === e.target?.value || ''
              ) || null;
            if (option) {
              goToSiteOption(option);
            }
          }
        }
      }}
      disabled={!hasSiteOptions}
      getOptionLabel={(option: any) => ellipsizeMiddle(option?.siteName || '')}
      onClose={() => setSearchTerm('')}
      popupIcon={
        !hasSiteOptions ? null : (
          <IconWrapper
            iconName={'chevron-right'}
            className={`${styles.BreadCrumb__Icon} ${
              isSafari ? styles.BreadCrumb__Icon__Safari : ''
            }`}
          />
        )
      }
      PopperComponent={({ children, ...props }) => {
        return (
          <Popper
            {...props}
            id="breadcrumbs-popper"
            className={`${isEndUser ? 'enduser-breadcrumbs-popper' : ''} ${
              isSafari ? 'is_safari_popper sites' : ''
            } ${searchTerm?.length > 0 ? 'typing' : ''}`}
            style={{
              width: 300,
              borderRadius: 12,
              marginTop: 8,
              ...(isEndUser ? { zIndex: 20000001 } : {}),
            }}
            placement={isEndUser ? 'bottom-start' : 'left'}
            modifiers={
              isEndUser
                ? [
                    {
                      name: 'offset',
                      options: { offset: [0, 40] },
                    },
                    {
                      name: 'preventOverflow',
                      options: { padding: 16 },
                    },
                  ]
                : undefined
            }
            anchorEl={document.getElementById('autocomplete-sites')}
          >
            {children}
          </Popper>
        );
      }}
      renderInput={(params) =>
        isMemberAggregate || (metadata?.selectedEntity as any)?.pod ? (
          <TextField
            {...params}
            variant="standard"
            onChange={(ev: any) => setSearchTerm(ev.target.value)}
            id="autocomplete-sites-input"
            className={styles.BreadCrumb__Input}
            label={displaySiteName || (t('widgets.dropdown.sites') as string)}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                const first = document.querySelector(
                  '#breadcrumbs-popper [role="option"] span'
                ) as HTMLElement | null;
                if (first) {
                  e.preventDefault();
                  first.click();
                }
              }
            }}
            placeholder={
              displaySiteName || (t('widgets.dropdown.sites') as string)
            }
            inputRef={(el) => {
              inputRefSites.current = el;
              const r = (params.inputProps as any).ref;
              if (typeof r === 'function') r(el);
              else if (r && 'current' in r)
                (r as React.MutableRefObject<HTMLInputElement | null>).current =
                  el;
            }}
            inputProps={{
              ...params.inputProps,
              ...(isSafari
                ? { style: { width: inputPx + 3.5 + 'px' } as any }
                : {}),
            }}
          ></TextField>
        ) : (
          <></>
        )
      }
      renderOption={(props, option, { selected }) => (
        <ListItem
          {...props}
          className={`${
            option?.siteId === params.id
              ? styles.Autocomplete__List__Item__Active
              : styles.Autocomplete__List__Item
          }`}
          value={option.value}
        >
          <span
            style={{ cursor: 'pointer' }}
            onClick={(ev: any) => {
              unFocusChild('autocomplete-search');
              goToSiteOption(option);
            }}
          >
            {ellipsizeMiddle(option?.siteName || '')}
            {!option?.__allSites && (
              <>
                <br />
                <small> {ellipsizeMiddle(option?.pod || '')} </small>
              </>
            )}
          </span>
        </ListItem>
      )}
    />
  );
}

// Generic, data-agnostic header dropdown used by mobile components
export interface HeaderEntityDropdownProps<T> {
  items: T[];
  selected: T | null;
  getLabel: (item: T) => string;
  getId?: (item: T) => string;
  getDescription?: (item: T) => string;
  onSelect: (item: T) => void;
  disabled?: boolean;
  singleItem?: boolean;
  noOptionsText: string;
  fullWidth?: boolean;
  variant?: 'default' | 'endUserWeb' | 'invoices';
}

export function HeaderEntityDropdown<T>(props: HeaderEntityDropdownProps<T>) {
  const {
    items,
    selected,
    getLabel,
    getId,
    getDescription,
    onSelect,
    disabled,
    singleItem,
    noOptionsText,
    fullWidth,
    variant = 'default',
  } = props;

  const isEndUserWeb = variant === 'endUserWeb';
  // FLEXO-6112 (#6): don't mid-truncate the invoices header name with a
  // character-index "..." (it cut mid-word, e.g. "Casa 1 Appar...nto piccolo").
  // Return the full name and let the CSS single-line ellipsis on the span/input
  // crop it cleanly at the available width, preserving the original casing.
  const displayLabel = useCallback(
    (item: T) => getLabel(item),
    [getLabel]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [textWidth, setTextWidth] = useState<number>(0);
  // FLEXO-6112 (#6): fixed width budget for the invoices selector name — the
  // space left in the title row after the chevron + close (X) button. Decoupled
  // from `textWidth` so a short name isn't trimmed by MUI's internal input
  // padding, while a long name still ellipsises at this budget.
  const [nameMaxWidth, setNameMaxWidth] = useState<number>(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const mirrorRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasAlreadyFocusedRef = useRef(false);

  // Calculate responsive maxWidth based on screen size
  const getResponsiveMaxWidth = () => {
    if (!fullWidth) return 'fit-content';
    const screenWidth = window?.innerWidth || 0;
    // iPhone SE and smaller: use more space
    if (screenWidth <= 390) return '100%';
    // Larger phones: use less to prevent overflow
    return '88%';
  };

  const filtered = useMemo(() => {
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) return items;
    // FLEXO-6088: match the row title AND its subtitle (site POD / community code),
    // so typing a POD like "CH1..." finds the site instead of clearing the list.
    const matches = (it: any) => {
      if (!it) return false;
      const label = String(getLabel(it as T) ?? '').toLowerCase();
      const description = getDescription
        ? String(getDescription(it as T) ?? '').toLowerCase()
        : '';
      return label.includes(term) || description.includes(term);
    };
    const list = items as any[];
    const keep = new Set<number>();
    list.forEach((it, i) => {
      if (!matches(it)) return;
      keep.add(i);
      // Keep the parent community header of a matched nested site so the
      // result isn't an orphaned, indented row.
      if (it?.__isChild) {
        for (let j = i - 1; j >= 0; j--) {
          if (!list[j]?.__isChild) {
            keep.add(j);
            break;
          }
        }
      }
    });
    return list.filter((_, i) => keep.has(i)) as T[];
  }, [items, getLabel, getDescription, searchTerm]);

  const controlledValue = (selected ?? undefined) as unknown as NonNullable<T>;

  // Measure text width reliably on iOS by using a hidden mirror element
  useLayoutEffect(() => {
    const measure = () => {
      const el = mirrorRef.current;
      if (!el) return;
      const str = (searchTerm ||
        (selected ? displayLabel(selected as T) : '')) as string;
      el.textContent = str;

      const raw = el.offsetWidth + 8;
      const vw = window?.innerWidth || 0;
      let maxLeft = fullWidth
        ? Math.max(0, Math.floor(vw * 0.8) + 12)
        : Math.max(0, Math.floor(vw * 0.8) - 24);
      // FLEXO-6112 (#6): in the invoices header the chevron is pinned at
      // `left: textWidth` and shares the title row with the close (X) button.
      // Give the name a fixed width budget = the row minus the chevron + X + the
      // drawer padding. The input's max-width uses this budget (so a SHORT name
      // is never trimmed by MUI's internal input padding), while the chevron caps
      // at the budget minus that padding (~20px) so on a LONG name it lands right
      // after the ellipsis instead of sliding under the X.
      if (variant === 'invoices') {
        const drawerPadding = vw <= 601 ? 80 : 48;
        const controlsReserve = 56;
        const budget = Math.max(0, vw - drawerPadding - controlsReserve);
        const MUI_INPUT_PADDING = 20;
        setNameMaxWidth(budget);
        maxLeft = Math.min(maxLeft, Math.max(0, budget - MUI_INPUT_PADDING));
      }
      setTextWidth(Math.min(raw, maxLeft));
    };
    // Wait for web fonts to be ready on Safari/iOS to avoid fallback metrics
    const fonts = (document as any)?.fonts;
    if (fonts && fonts.ready && typeof fonts.ready.then === 'function') {
      fonts.ready.then(measure);
    } else {
      measure();
    }
    // Recompute on window resize (DPR/viewport changes on iOS)
    window.addEventListener('resize', measure);
    // Recompute whenever the mirror's rendered width changes — covers late web-font
    // loads and dev HMR style updates that change the font WITHOUT re-running this
    // effect (its deps don't change on a pure CSS update), which would otherwise
    // leave the chevron pinned to a stale, drifted position.
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && mirrorRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(mirrorRef.current);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [searchTerm, selected, displayLabel, fullWidth, variant]);

  // When there's only one item, render plain text instead of input
  if (singleItem) {
    const label = selected ? displayLabel(selected as T) : '';
    return (
      <span
        className={`${styles.BreadCrumb} ${styles.BreadCrumb__Mobile} ${styles.BreadCrumb__SingleItem}`}
        style={{
          display: 'block',
          paddingTop: '20px',
          paddingBottom: '0',
          fontFamily: 'var(--hive-font)',
          fontSize: '24px',
          fontWeight: 'var(--semi-bold)',
          lineHeight: '32px',
          color: 'var(--hive-greyscale-900)',
          maxWidth: fullWidth ? '100%' : '80vw',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  }

  return (
    <Autocomplete
      // FLEXO-6096 #3: faithful port of the admin/supervisor breadcrumb interaction.
      // The end-user web selector lets MUI own the open state (uncontrolled) and opens
      // on focus, exactly like CommunitiesDropDown/SitesDropDown — so the input keeps
      // focus while the list is open and MUI's native keyboard navigation (↑/↓ move the
      // highlight, Enter selects, Esc closes) and mouse hover work for free. Other
      // variants keep the controlled-open behaviour. `onOpen`/`onClose` still mirror the
      // open state so the chevron icon direction stays in sync.
      {...(isEndUserWeb ? { openOnFocus: true } : { open })}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearchTerm('');
        setIsInputFocused(false);
        // Blur the input to completely deactivate it (controlled variants only).
        if (!isEndUserWeb && inputRef.current) {
          inputRef.current.blur();
        }
      }}
      inputValue={searchTerm}
      onInputChange={(_e, val) => setSearchTerm(val || '')}
      blurOnSelect
      disableClearable
      disablePortal={false}
      clearOnBlur
      id="header-entity-dropdown"
      className={`${styles.BreadCrumb} ${styles.BreadCrumb__Mobile} ${
        variant === 'endUserWeb' ? styles.BreadCrumb__EndUserWeb : ''
      } ${singleItem ? styles.BreadCrumb__SingleItem : ''}`}
      options={filtered as T[]}
      // FLEXO-6088: `filtered` already matches title + subtitle (POD/code) against
      // the search term. Disable MUI's built-in label-only filter so it doesn't
      // re-drop POD matches (typing "CH1..." was clearing the list).
      filterOptions={(opts) => opts}
      value={controlledValue}
      disabled={!!disabled}
      // FLEXO-6099: crop long names in the DISPLAY layer only — `filtered` above
      // still matches against the full `getLabel` string, so searching by the
      // middle of a name keeps working.
      getOptionLabel={(option) => displayLabel(option as T)}
      onChange={(_e, value) => {
        if (value) {
          onSelect(value as T);
        }
        setOpen(false);
      }}
      noOptionsText={
        (
          <span className={`${styles.Autocomplete__List__Item}`}>
            {noOptionsText}
          </span>
        ) as unknown as string
      }
      popupIcon={null}
      sx={{
        // maxWidth: getResponsiveMaxWidth(),
        maxHeight: '48px',
        width: '100%',
      }}
      PopperComponent={({ children, ...popperProps }) => (
        <Popper
          {...popperProps}
          placement="bottom-start"
          id="breadcrumbs-popper-mobile"
          className={`${styles.Autocomplete__MobilePopper} ${
            variant === 'endUserWeb'
              ? styles.Autocomplete__MobilePopper__EndUserWeb
              : ''
          } ${
            variant === 'invoices'
              ? styles.Autocomplete__MobilePopper__Invoices
              : ''
          }`}
        >
          {children}
        </Popper>
      )}
      renderInput={(params) => (
        <div className={styles.HeaderEntityDropdown__InputWrapper}>
          <TextField
            {...params}
            variant="standard"
            className={styles.BreadCrumb__Input}
            onChange={(ev) =>
              setSearchTerm((ev?.target as HTMLInputElement)?.value || '')
            }
            onFocus={() => {
              setSearchTerm('');
              setIsInputFocused(true);
            }}
            onBlur={() => {
              setIsInputFocused(false);
              wasAlreadyFocusedRef.current = false;
            }}
            onMouseDown={() => {
              // Store the current focus state before the click
              wasAlreadyFocusedRef.current = isInputFocused;
            }}
            onClick={() => {
              // Use setTimeout to allow focus event to complete first
              setTimeout(() => {
                // If input was already focused when clicked, blur it
                if (wasAlreadyFocusedRef.current && inputRef.current) {
                  inputRef.current.blur();
                  setIsInputFocused(false);
                  setOpen(false);
                  setSearchTerm('');
                }
              }, 0);
            }}
            placeholder={
              (selected ? displayLabel(selected as T) : '') as string
            }
            label={(selected ? displayLabel(selected as T) : '') as string}
            sx={{
              // FLEXO-6112 (#6): pin the text box to the same width the chevron is
              // positioned at (`left: textWidth`) so a long site name ellipsises
              // right before the chevron instead of running underneath it. Falls
              // back to the previous cap until the width has been measured.
              maxWidth:
                variant === 'invoices' && nameMaxWidth > 0
                  ? `${nameMaxWidth}px`
                  : fullWidth
                    ? '100%'
                    : '80vw',
            }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              inputRef: inputRef,
              style: {
                display: 'flex',
                alignItems: 'center',
                paddingRight:
                  // FLEXO-6112 (#6): the invoices input is width-capped to
                  // `textWidth` and the chevron is pinned at `left: textWidth`.
                  // A large right padding would (a) eat into that width and trim
                  // even short names, and (b) leave a gap between the name and the
                  // chevron. Keep it minimal so the chevron hugs the name.
                  variant === 'invoices'
                    ? '4px'
                    : fullWidth && (window?.innerWidth || 0) <= 390
                      ? '8px'
                      : '20px',
              },
            }}
          />
          {(items?.length || 0) > 1 && (
            <div
              className={`${styles.HeaderEntityDropdown__Chevron} ${
                variant === 'invoices'
                  ? styles.HeaderEntityDropdown__Chevron__Invoices
                  : ''
              }`}
              style={
                // FLEXO-6076/6077: the end-user input is content-sized (inline-flex),
                // so pin the chevron to the wrapper's right edge — it lands right
                // after the name without measuring text width (no font/HMR drift).
                // Other variants keep the measured position.
                variant === 'endUserWeb'
                  ? {
                      right: 0,
                      left: 'auto',
                      marginTop: '-2px',
                      transform: 'none',
                    }
                  : { left: textWidth }
              }
              onClick={() => {
                if (isEndUserWeb) {
                  // Toggle through the input so MUI manages open + keeps focus (native
                  // keyboard nav). Focus opens (openOnFocus); blur closes.
                  if (open) inputRef.current?.blur();
                  else inputRef.current?.focus();
                } else {
                  setOpen((o) => !o);
                }
              }}
            >
              <IconWrapper
                iconName={open ? 'chevron-left' : 'chevron-right'}
                className={`${styles.BreadCrumb__Icon}`}
              />
            </div>
          )}
          <span
            ref={mirrorRef}
            className={styles.HeaderEntityDropdown__Measure}
          >
            {(selected ? displayLabel(selected as T) : '') as string}
          </span>
        </div>
      )}
      renderOption={(listProps, option) => {
        const title = displayLabel(option as T);
        const description = getDescription ? getDescription(option as T) : '';
        const isAllSites = !!(option as any)?.__allSites;
        // FLEXO-6088 #1a: nested entity selector — community rows act as headers
        // and their sites are rendered indented underneath.
        const isChild = !!(option as any)?.__isChild;
        const isHeader = (option as any)?.kind === 'community';
        const isActive = !!(
          getId &&
          selected &&
          getId(option as T) === getId(selected as T)
        );
        return (
          <ListItem
            {...listProps}
            // Keep MUI's option className (carries `Mui-focused`, applied to the
            // keyboard-highlighted row) so ↑/↓ produce a visible highlight (FLEXO-6096 #3).
            className={`${(listProps as any)?.className || ''} ${
              styles.Autocomplete__List__Item__Mobile
            } ${
              isActive ? styles.Autocomplete__List__Item__Active__Mobile : ''
            } ${
              isAllSites
                ? styles.Autocomplete__List__Item__Mobile__AllSites
                : ''
            } ${
              isChild ? styles.Autocomplete__List__Item__Mobile__Child : ''
            } ${
              isHeader ? styles.Autocomplete__List__Item__Mobile__Header : ''
            }`}
          >
            <div
              className={`${styles.Autocomplete__List__Item__Mobile__Text} `}
            >
              <span
                className={`${styles.Autocomplete__List__Item__Mobile__Title} ${
                  isActive && 'color-primary-500'
                }`}
              >
                {title}
              </span>
              {description ? (
                <span
                  className={
                    styles.Autocomplete__List__Item__Mobile__Description
                  }
                >
                  {description}
                </span>
              ) : null}
            </div>
          </ListItem>
        );
      }}
    />
  );
}
